import React, { useState, useEffect, useMemo } from "react";
import { useTimer } from "react-timer-hook";
import { useForm } from "react-hook-form";
import { useSendOTPWithRecaptcha } from "../../../store/auth/otp.api";
import { PhoneInput } from "../../components/form/PhoneInput";
import { ROUTES } from "../../../constants/routes";
import OtpInput from "react-otp-input";
import { setPhoneNumberFormResolver } from "../../../constants/resolvers";
import { useRecaptchaDismissalDetector } from "../../../hooks/useRecaptchaDismissalDetector";
import { useVerifyOTPMutation } from "../../../store/auth/otp.api";
import { ResetPasswordForm } from "./forms/ResetPasswordForm";
import { useValidatePhoneMutation } from "../../../store/auth/auth.api";
import { useHistory } from "react-router-dom/";
import { DEFAULT_COUNTRY } from "../../../constants/country-config";

export default function RecoverPassword({ onBackToLogin }) {
  const [sendOTP] = useSendOTPWithRecaptcha();
  const [verifyOTP] = useVerifyOTPMutation();
  const [verifyPhoneNumber] = useValidatePhoneMutation();
  const form = useForm({ resolver: setPhoneNumberFormResolver });
  const { handleSubmit, setError, trigger, formState } = form;
  const { restart, seconds, isRunning } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {
      setTimerExpired(true);
      setIsTimerCompleted(true);
    }
  });
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const { isSubmitting } = formState;
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [isTimerCompleted, setIsTimerCompleted] = useState(false);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [otpValue, setOtpValue] = useState(Array(4).fill(""));
  const [step, setStep] = useState("setPhoneNumber");
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const history = useHistory();
  const [loader, setLoader] = useState(false);

  // Setup reCAPTCHA dismissal detection
  const { setupDismissalListener, cleanup } = useRecaptchaDismissalDetector({
    onDismissed: () => {
      setLoader(false);
      setIsButtonDisabled(false);
      setError("phoneNumber", {
        type: "recaptcha",
        message: "Security verification cancelled. Please try again."
      });
    }
  });

  const handleCreateAcc = () => {
    history.push(ROUTES.STUDENT_CREATE_ACCOUNT);
  };

  const onBlur = async (fieldName) => {
    if (fieldName === "phoneNumber") {
      if (phoneNumber && phoneNumber.length > 0) {
        if (phoneNumber.length !== selectedCountry.phoneLength) {
          setError("phoneNumber", {
            message: `Phone number must be ${selectedCountry.phoneLength} digits`
          });
        } else {
          clearErrors("phoneNumber");
        }
      } else if (!phoneNumber || phoneNumber.length === 0) {
        setError("phoneNumber", {
          message: `Phone number is required`
        });
      }
    } else {
      await trigger(fieldName);
    }
  };

  // Updated to accept country parameter
  const handlePhoneNumberChange = async (phoneNumber, country) => {
    setSelectedCountry(country);
    setValue("phoneNumber", phoneNumber);

    if (loader) return;

    // Show error while typing if length is less than required and not empty
    if (phoneNumber.length > 0 && phoneNumber.length < country.phoneLength) {
      setError("phoneNumber", {
        message: `Phone number must be ${country.phoneLength} digits`
      });
      setIsButtonDisabled(true);
      return;
    }

    // Show error when length exceeds
    if (phoneNumber.length > country.phoneLength) {
      setError("phoneNumber", {
        message: `Phone number must be ${country.phoneLength} digits`
      });
      setIsButtonDisabled(true);
      return;
    }

    // Exactly matches the required length
    if (phoneNumber.length === country.phoneLength) {
      clearErrors("phoneNumber");
      setIsButtonDisabled(false);

      const result = await verifyPhoneNumber({
        phoneNumber: phoneNumber,
        isdCode: country.isdCode
      });

      if (result?.data?.isVerified) {
        setError("phoneNumber", { message: "Phone number verification failed" });
        setIsButtonDisabled(false);
      } else {
        clearErrors("phoneNumber");
        setValue("phoneNumberVerified", true);
        setIsButtonDisabled(false);
      }
    }

    // If phone number is empty, clear errors
    if (phoneNumber.length === 0) {
      clearErrors("phoneNumber");
      setIsButtonDisabled(true);
    }
  };

  // Handle country change
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setValue("phoneNumber", "");
    clearErrors("phoneNumber");
    setIsButtonDisabled(true);

    if (isOtpSent) {
      setIsOtpSent(false);
      setShowOtpInput(false);
      setOtpValue(Array(4).fill(""));
      clearErrors("otpCode");
      setIsEditEnabled(false);
      restart();
      setIsTimerCompleted(true);
    }
  };

  const handleOtpCodeChange = (e) => {
    if (e.length >= 0 && e.length <= 3) {
      setError("otpCode", { type: "custom", message: "OTP must be exactly 4 digits." });
    } else {
      setError("otpCode", { type: "custom", message: "" });
    }
    const regex = /^[0-9]*$/;
    if (!regex.test(e)) {
      const otp = e.replace(/\D/g, "");
      setOtpValue([...otp]);
    } else {
      setOtpValue([...e]);
    }
    setIsButtonDisabled(e.length !== 4);
  };

  const clickSendOTP = async () => {
    setOtpValue(Array(4).fill(""));
    setError("otpCode", { type: "custom", message: "" });
    setIsButtonDisabled(true);
    setLoader(true);

    // Manual validation before sending OTP
    if (!phoneNumber || phoneNumber.length !== selectedCountry.phoneLength) {
      setError("phoneNumber", {
        message: `Phone number must be ${selectedCountry.phoneLength} digits`
      });
      setIsButtonDisabled(false);
      setLoader(false);
      return false;
    }

    clearErrors("phoneNumber");
    setIsTimerCompleted(false);

    const result = await verifyPhoneNumber({
      phoneNumber: phoneNumber,
      isdCode: selectedCountry.isdCode
    });

    if (!result?.data?.detail) {
      setShowOtpInput(false);
      setError("phoneNumber", { message: "Seems like you are a new user! Please create your account" });
      setIsButtonDisabled(true);
      setLoader(false);
      return false;
    } else {
      setShowOtpInput(true);
    }

    const phoneNumberIsValid = phoneNumber?.length === selectedCountry.phoneLength;

    if (phoneNumberIsValid && result?.data?.detail) {
      setupDismissalListener();
      try {
        const otpResult = await sendOTP({
          phoneNumber: phoneNumber,
          isdCode: selectedCountry.isdCode
        });

        if (otpResult?.data) {
          const time = new Date();
          time.setSeconds(time.getSeconds() + 30);
          restart(time);
          setIsEditEnabled(true);
          setLoader(false);
          return true;
        } else {
          const errorData = otpResult?.error?.data || otpResult?.error || otpResult;
          const errorMessage = errorData?.message || errorData?.detail || "Failed to send OTP";
          const errorDetail = errorData?.detail || "";

          if (errorDetail.includes("reCAPTCHA")) {
            setError("phoneNumber", {
              type: "recaptcha",
              message: "Security verification failed. Please try again."
            });
          } else {
            setError("phoneNumber", {
              type: "api",
              message: errorMessage
            });
          }
          setShowOtpInput(false);
          setIsEditEnabled(false);
          setIsButtonDisabled(false);
          setLoader(false);
          return false;
        }
      } catch (error) {
        setLoader(false);
        setIsButtonDisabled(false);

        const errorData = error?.error?.data || error?.error || error;
        const errorMessage =
          errorData?.message || errorData?.detail || "An unexpected error occurred. Please try again.";

        setError("phoneNumber", {
          type: "api",
          message: errorMessage
        });
        setShowOtpInput(false);
        setIsEditEnabled(false);
        return false;
      }
    }
    return false;
  };

  const clickVerifyOTP = async () => {
    const otpString = otpValue.join("");
    setIsTimerCompleted(true);

    await trigger("otpCode");

    if (otpString.length === 4) {
      console.log("otpString:", otpString);
      setValue("otpCode", otpString);
      const result = await verifyOTP({
        otpCode: otpString,
        phoneNumber: phoneNumber,
        isdCode: selectedCountry.isdCode
      });

      console.log(result, "result");
      if (result?.data && otpString && phoneNumber) {
        setStep("resetPassword");
      } else if (result?.error?.data?.message?.toLowerCase().includes("user does not exist")) {
        setError("phoneNumber", { type: "custom", message: "Mobile number is not registered. Please register." });
        setIsButtonDisabled(true);
      } else if (result?.error?.data?.message?.includes("Incorrect OTP")) {
        setError("otpCode", { type: "custom", message: "OTP is incorrect or not valid" });
        setIsButtonDisabled(true);
      } else {
        console.log(result);
        setError("otpCode", { type: "custom", message: "Oops! Something went wrong. Please try again.." });
        setIsButtonDisabled(true);
      }
    }
  };

  const handleEditNumberClick = () => {
    setIsEditEnabled(false);
    setShowOtpInput(false);
    setIsOtpSent(false);
    setValue("phoneNumber", "");
    setOtpValue(Array(4).fill(""));
    clearErrors("phoneNumber");
    Array(4)
      .fill("")
      .forEach((_, index) => {
        clearErrors(`otpCode[${index}]`);
      });
    restart();
    setIsTimerCompleted(true);
  };

  const handletoggle = () => {
    onBackToLogin();
  };

  const {
    formState: { errors },
    watch,
    setValue,
    clearErrors
  } = form;
  const [phoneNumber, ...otpCodes] = watch(["phoneNumber", ...Array(4).fill("otpCode")]);

  // Dynamic phone number validation based on selected country
  const phoneNumberIsValid = useMemo(
    () => phoneNumber?.length === selectedCountry.phoneLength,
    [phoneNumber, selectedCountry]
  );

  const disabled = useMemo(() => {
    return {
      phoneForm: isRunning || isSubmitting,
      otpForm: !isRunning || isSubmitting,
      verifyButton: false
    };
  }, [isRunning, isSubmitting, phoneNumberIsValid]);

  useEffect(() => {
    if (timerExpired && isOtpSent) {
      setTimerExpired(false);
    }
    if (timerExpired && !isOtpSent) {
      setShowOtpInput(false);
      setTimerExpired(false);
    }

    return cleanup;
  }, [timerExpired, isOtpSent, cleanup]);

  const handleButtonAction = async () => {
    if (!isButtonDisabled) {
      if (isOtpSent) {
        await clickVerifyOTP();
      } else {
        const success = await clickSendOTP();
        if (success) {
          setIsOtpSent(true);
          setIsTimerCompleted(false);
          setIsButtonDisabled(true);
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(clickVerifyOTP)}>
      <div className="membership__card mx-auto ms-md-auto">
        {step === "setPhoneNumber" ? (
          <h3 className="tab-heading">Forgot Password</h3>
        ) : (
          <h3 className="tab-heading">Reset Password</h3>
        )}
        <div className="tab-content"></div>
        {step === "setPhoneNumber" ? (
          <>
            <div className="row">
              <div className="col-12 position-relative mb-12">
                <label className="tab-lable">Phone Number</label>
                <PhoneInput
                  form={form}
                  disabled={isEditEnabled || loader}
                  handleChange={handlePhoneNumberChange}
                  onBlur={() => onBlur("phoneNumber")}
                  handleEditNumberClick={handleEditNumberClick}
                  isEditEnabled={isEditEnabled}
                  onCountryChange={handleCountryChange}
                />
                {errors.phoneNumber && <span className="error--strike--text">{errors.phoneNumber.message}</span>}
                {disabled.verifyButton &&
                  otpCodes.map((_, index) => {
                    return (
                      errors[`otpCode[${index}]`] && (
                        <span className="error--strike--text" key={index}>
                          {errors[`otpCode[${index}]`].message}
                        </span>
                      )
                    );
                  })}
              </div>
              {showOtpInput && (
                <div>
                  {!disabled.verifyButton && (
                    <div>
                      <label className="tab-lable">Enter OTP</label>
                      <div className="col-12 position-relative mb-12">
                        <div className="d-flex align-items-center">
                          <OtpInput
                            inputType="number"
                            skipDefaultStyles={false}
                            shouldAutoFocus="false"
                            value={otpValue.join("")}
                            onChange={(e) => handleOtpCodeChange(e)}
                            numInputs={4}
                            separator={<span> </span>}
                            isDisabled={disabled.otpForm}
                            renderInput={(props, index) => (
                              <input
                                {...props}
                                key={index}
                                type="text"
                                required
                                className={`otp-input-box ${
                                  errors.otpCode?.message ? "otp-error-strick" : "highlight"
                                }`}
                              />
                            )}
                          />
                          <div className="resend_otp">
                            {!isTimerCompleted && (
                              <span>
                                Resend OTP <br />({seconds} Sec.)
                              </span>
                            )}
                            {isTimerCompleted && (
                              <button className="resend-btn" onClick={clickSendOTP}>
                                Resend OTP
                              </button>
                            )}
                          </div>
                        </div>
                        {errors.otpCode && <span className="error--strike--text">{errors.otpCode.message}</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              className="button--membersubmit w-100"
              onClick={handleButtonAction}
              disabled={isButtonDisabled}
            >
              {isOtpSent ? "Continue" : "Send OTP"}
            </button>
          </>
        ) : step === "resetPassword" ? (
          <ResetPasswordForm selectedCountry={selectedCountry} phoneNumber={phoneNumber} />
        ) : null}
        <div className="hr-box">
          <hr className="hr-line" />
          <span>Or</span>
          <hr className="hr-line" />
        </div>
        <div className="col-12 mt-3 mb-3 pb-2 text-center">
          <a className="gard--textflow gard--textflow-mob" style={{ cursor: "pointer" }} onClick={handletoggle}>
            Back to Login
          </a>
        </div>
        <div className="col-12 create-ac-link">
          <p className="login--supporttext">
            Don&apos;t have a Genius account?{" "}
            <span className="gard--textflow create-acoount" onClick={handleCreateAcc}>
              create account
            </span>
          </p>
        </div>
      </div>
    </form>
  );
}
